import OpenInNewIcon from '@material-design-icons/svg/filled/open_in_new.svg?react';
import { useStore } from '@tanstack/react-store';
import { isLeft, isRight } from 'fp-ts/Either';
import { use, useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import type { KybStatus } from '../../../api/src/types/database';
import { TRPCContext } from '../contexts/TRPC';
import { useExternalSyncStatus } from '../hooks/useExternalSyncStatus';
import { formatEasternTimeFirstDateTime } from '../lib/formatters';
import {
  type KybSubmissionDetail,
  kybManagementStore,
  selectSubmission,
  setLoading,
  setPage,
  setPaginationInfo,
  setSorting,
  setSubmissions,
  setSummary,
  updateFilter,
} from '../stores/kybManagementStore';
import { captureAndReportError } from '../utils/errorReporting';
import { DataTable } from './common/DataTable';
import { FilterBar, SelectFilter } from './common/FilterBar';
import { Pagination } from './common/Pagination';
import { ProviderSyncStatus } from './common/ProviderSyncStatus';
import { KybSubmissionDetail as KybSubmissionDetailModal } from './KybSubmissionDetail';

const formatDateTime = (dateString?: string | null) =>
  formatEasternTimeFirstDateTime(dateString);

type KybStatusBadgeMeta = {
  label: string;
  className: string;
};

const KYB_STATUS_BADGE_META: Record<string, KybStatusBadgeMeta> = {
  NotStarted: {
    label: 'Not Started',
    className: 'bg-neutral-500/10 text-neutral-400 border-neutral-500/20',
  },
  InProgress: {
    label: 'In Progress',
    className: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  },
  UnderReview: {
    label: 'Under Review',
    className: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
  },
  Approved: {
    label: 'Approved',
    className: 'bg-green-500/10 text-green-400 border-green-500/20',
  },
  Rejected: {
    label: 'Rejected',
    className: 'bg-red-500/10 text-red-400 border-red-500/20',
  },
  RequiresAdditionalInfo: {
    label: 'Requires Info',
    className: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
  },
};

const SYNC_HEALTH_COLORS: Record<string, string> = {
  healthy: 'bg-green-500',
  warning: 'bg-yellow-500',
  error: 'bg-red-500',
};

const getStatusBadgeColor = (status: KybStatus) =>
  KYB_STATUS_BADGE_META[status]?.className ??
  'bg-neutral-500/10 text-neutral-300 border-neutral-500/20';

const formatStatusLabel = (status: KybStatus) =>
  KYB_STATUS_BADGE_META[status]?.label ?? status;

export function KybManagementPanel() {
  const trpcContext = use(TRPCContext);
  const latestRequestRef = useRef(0);
  const trpcClient = trpcContext?.client;

  const submissions = useStore(
    kybManagementStore,
    (state) => state.submissions,
  );
  const isLoading = useStore(kybManagementStore, (state) => state.ui.isLoading);
  const filters = useStore(kybManagementStore, (state) => state.filters);
  const sorting = useStore(kybManagementStore, (state) => state.sorting);
  const pagination = useStore(kybManagementStore, (state) => state.pagination);
  const summary = useStore(kybManagementStore, (state) => state.summary);
  const selectedSubmissionId = useStore(
    kybManagementStore,
    (state) => state.ui.selectedSubmissionId,
  );

  const { syncStatus } = useExternalSyncStatus({
    componentName: 'KybManagementPanel',
  });

  const loadSubmissions = useCallback(async () => {
    const requestId = latestRequestRef.current + 1;
    latestRequestRef.current = requestId;

    if (!trpcClient) {
      if (requestId === latestRequestRef.current) {
        setLoading(false);
      }
      return;
    }

    const requestedPage = pagination.currentPage;

    try {
      setLoading(true);

      const apiSorting =
        sorting.length > 0
          ? sorting
              .filter((sort) =>
                [
                  'status',
                  'submittedAt',
                  'reviewCompletedAt',
                  'email',
                ].includes(sort.id),
              )
              .map((sort) => ({
                id: sort.id as
                  | 'status'
                  | 'submittedAt'
                  | 'reviewCompletedAt'
                  | 'email',
                desc: sort.desc,
              }))
          : undefined;

      const result = await trpcClient.getAllKybSubmissions.query({
        page: requestedPage,
        limit: 20,
        status: filters.status === 'all' ? undefined : filters.status,
        search: filters.search || undefined,
        sortBy: apiSorting?.[0]?.id || 'submittedAt',
        sortOrder: apiSorting?.[0]?.desc ? 'desc' : 'asc',
      });

      if (requestId !== latestRequestRef.current) {
        return;
      }

      if (isRight(result)) {
        setSubmissions(result.right.submissions);
        setPaginationInfo(
          requestedPage,
          result.right.pagination.totalPages,
          result.right.pagination.total,
        );
        setSummary(result.right.summary);
      } else if (isLeft(result)) {
        toast.error(result.left.message || 'Failed to load KYB submissions');
      }
    } catch (error) {
      if (requestId !== latestRequestRef.current) {
        return;
      }

      captureAndReportError(error, {
        operation: 'admin.loadKybSubmissions',
        component: 'KybManagementPanel',
        filters: {
          statusFilter: filters.status,
          searchQuery: filters.search,
        },
        page: requestedPage,
      });
      toast.error('Failed to load KYB submissions');
    } finally {
      if (requestId === latestRequestRef.current) {
        setLoading(false);
      }
    }
  }, [
    filters.search,
    filters.status,
    pagination.currentPage,
    sorting,
    trpcClient,
  ]);

  useEffect(() => {
    void loadSubmissions();
  }, [loadSubmissions]);

  const columns = [
    {
      key: 'user',
      header: 'User',
      headerClassName: 'text-xs font-semibold text-neutral-300',
      size: 250,
      enableSorting: false,
      accessor: (submission: KybSubmissionDetail) => (
        <div className="space-y-1">
          <div className="text-sm font-medium text-white truncate">
            {submission.user.fullName}
          </div>
          <div className="text-xs text-neutral-400 truncate">
            {submission.user.email}
          </div>
          <span
            className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide ${
              submission.publisher.type === 'Individual'
                ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20'
                : 'bg-purple-500/10 text-purple-400 border border-purple-500/20'
            }`}
          >
            {submission.publisher.type}
          </span>
        </div>
      ),
    },
    {
      key: 'publisher',
      header: 'Publisher',
      headerClassName: 'text-xs font-semibold text-neutral-300',
      size: 200,
      enableSorting: false,
      accessor: (submission: KybSubmissionDetail) => (
        <div className="space-y-1">
          <div className="text-sm text-white font-medium truncate">
            {submission.publisher.name}
          </div>
          {submission.publisher.website && (
            <a
              href={submission.publisher.website}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-xs text-teal-400 hover:text-teal-300 transition-colors truncate block"
            >
              {submission.publisher.website.replace(/^https?:\/\//, '')}
            </a>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      headerClassName: 'text-xs font-semibold text-neutral-300',
      size: 140,
      enableSorting: true,
      accessor: (submission: KybSubmissionDetail) => (
        <span
          className={`px-2 py-1 rounded text-xs font-medium border ${getStatusBadgeColor(submission.status)}`}
        >
          {formatStatusLabel(submission.status)}
        </span>
      ),
    },
    {
      key: 'reviewStatus',
      header: 'Review Status',
      headerClassName: 'text-xs font-semibold text-neutral-300',
      size: 140,
      enableSorting: false,
      accessor: (submission: KybSubmissionDetail) => {
        const reviewStatus = submission.sumsubApplicant?.reviewStatus;
        if (!reviewStatus) {
          return <span className="text-neutral-500 text-xs">—</span>;
        }

        const getReviewStatusColor = (status: string) => {
          const normalized = status.toLowerCase();
          if (
            normalized.includes('approved') ||
            normalized.includes('completed')
          ) {
            return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
          }
          if (normalized.includes('pending') || normalized.includes('init')) {
            return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
          }
          if (normalized.includes('reject')) {
            return 'bg-red-500/10 text-red-400 border-red-500/20';
          }
          return 'bg-neutral-500/10 text-neutral-400 border-neutral-500/20';
        };

        return (
          <span
            className={`px-2 py-1 rounded text-xs font-medium border ${getReviewStatusColor(reviewStatus)}`}
          >
            {reviewStatus}
          </span>
        );
      },
    },
    {
      key: 'submittedAt',
      header: 'Submitted',
      headerClassName: 'text-xs font-semibold text-neutral-300',
      className: '!px-3',
      size: 120,
      enableSorting: true,
      sortDescFirst: true,
      accessor: (submission: KybSubmissionDetail) => (
        <div className="text-xs text-neutral-200 font-medium">
          {formatDateTime(submission.submittedAt)}
        </div>
      ),
    },
    {
      key: 'reviewCompletedAt',
      header: 'Review Completed',
      headerClassName: 'text-xs font-semibold text-neutral-300',
      className: '!px-3',
      size: 120,
      enableSorting: true,
      sortDescFirst: true,
      accessor: (submission: KybSubmissionDetail) => (
        <div className="text-xs text-neutral-200 font-medium">
          {formatDateTime(
            submission.reviewCompletedAt ||
              submission.updatedAt ||
              submission.sumsubApplicant?.sumsubUpdatedAt,
          )}
        </div>
      ),
    },
    {
      key: 'sync',
      header: 'Sync',
      headerClassName: 'text-xs font-semibold text-neutral-300',
      size: 100,
      enableSorting: false,
      accessor: (submission: KybSubmissionDetail) => {
        const health = submission.syncStatus?.syncHealth || 'unknown';
        const lastSync =
          submission.sumsubApplicant?.lastSyncedAt ||
          submission.syncStatus?.lastSyncTimestamp;

        const healthColor = SYNC_HEALTH_COLORS[health] ?? 'bg-neutral-500';

        const formatSyncTime = () => {
          if (!lastSync) return 'Never';
          const date = new Date(lastSync);
          const now = new Date();
          const diff = now.getTime() - date.getTime();
          const minutes = Math.floor(diff / 60000);
          if (minutes < 1) return 'Just now';
          if (minutes < 60) return `${minutes}m ago`;
          const hours = Math.floor(minutes / 60);
          if (hours < 24) return `${hours}h ago`;
          return date.toLocaleDateString();
        };

        return (
          <div className="flex items-center gap-2">
            <div
              className={`w-2 h-2 rounded-full ${healthColor}`}
              title={`Sync ${health}${
                submission.syncStatus?.errorMessage
                  ? `: ${submission.syncStatus.errorMessage}`
                  : ''
              }`}
            />
            <span className="text-xs text-neutral-400">{formatSyncTime()}</span>
          </div>
        );
      },
    },
    {
      key: 'sumsub',
      header: 'Sumsub',
      headerClassName: 'text-xs font-semibold text-neutral-300',
      size: 100,
      enableSorting: false,
      accessor: (submission: KybSubmissionDetail) => {
        if (!submission.sumsubApplicant?.sumsubId) {
          return <span className="text-neutral-500 text-xs">—</span>;
        }

        const sumsubUrl = `https://cockpit.sumsub.com/checkus/applicant/${submission.sumsubApplicant.sumsubId}/basicInfo?clientId=solana`;

        return (
          <a
            href={sumsubUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-teal-400 hover:text-teal-300 border border-teal-400/30 hover:border-teal-400/50 rounded-lg transition-colors"
            title="View in Sumsub Dashboard"
          >
            <OpenInNewIcon className="size-3.5 fill-current" />
            <span>View</span>
          </a>
        );
      },
    },
  ];

  return (
    <>
      <div className="flex-1 overflow-y-auto p-4 md:p-6">
        <div className="max-w-7xl mx-auto space-y-4">
          <ProviderSyncStatus syncStatus={syncStatus} providerName="sumsub" />

          {summary && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="bg-neutral-900/40 rounded-lg border border-neutral-800/60 p-4">
                <div className="text-sm text-neutral-400">
                  Total Submissions
                </div>
                <div className="text-2xl font-semibold text-white">
                  {pagination.total}
                </div>
              </div>
              <div className="bg-neutral-900/40 rounded-lg border border-neutral-800/60 p-4">
                <div className="text-sm text-neutral-400">Approval Rate</div>
                <div className="text-2xl font-semibold text-white">
                  {summary.approvalRate.toFixed(1)}%
                </div>
              </div>
              <div className="bg-neutral-900/40 rounded-lg border border-neutral-800/60 p-4">
                <div className="text-sm text-neutral-400">Under Review</div>
                <div className="text-2xl font-semibold text-white">
                  {summary.totalByStatus.UnderReview}
                </div>
              </div>
              <div className="bg-neutral-900/40 rounded-lg border border-neutral-800/60 p-4">
                <div className="text-sm text-neutral-400">Approved</div>
                <div className="text-2xl font-semibold text-green-400">
                  {summary.totalByStatus.Approved}
                </div>
              </div>
            </div>
          )}

          <FilterBar
            searchValue={filters.search}
            onSearchChange={(value) => updateFilter('search', value)}
            searchPlaceholder="Search by email, name, or applicant ID..."
          >
            <SelectFilter
              value={filters.status}
              onChange={(value) =>
                updateFilter('status', value as KybStatus | 'all')
              }
              options={[
                { value: 'all', label: 'All Status' },
                { value: 'NotStarted', label: 'Not Started' },
                { value: 'InProgress', label: 'In Progress' },
                { value: 'UnderReview', label: 'Under Review' },
                { value: 'Approved', label: 'Approved' },
                { value: 'Rejected', label: 'Rejected' },
                { value: 'RequiresAdditionalInfo', label: 'Requires Info' },
              ]}
            />
          </FilterBar>

          <div className="md:bg-neutral-900/40 md:rounded-lg md:border md:border-neutral-800/60 overflow-hidden">
            <div className="px-5 py-3 border-b border-neutral-800/60 hidden md:block">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-neutral-300">
                  {pagination.total}{' '}
                  {pagination.total === 1 ? 'Submission' : 'Submissions'}
                </h3>
                {pagination.totalPages > 1 && (
                  <div className="text-xs text-neutral-500">
                    Page {pagination.currentPage} of {pagination.totalPages}
                  </div>
                )}
              </div>
            </div>

            <DataTable
              data={submissions}
              columns={columns}
              isLoading={isLoading}
              emptyMessage="No submissions found"
              onRowClick={(submission) => selectSubmission(submission.id)}
              selectedRowId={selectedSubmissionId ?? undefined}
              getRowId={(submission) => submission.id}
              enableSorting={true}
              sorting={sorting}
              onSortingChange={(updater) => {
                setSorting(updater);
              }}
              renderMobileItem={(submission) => (
                <div className="space-y-3">
                  <div className="flex justify-between items-start">
                    <div className="space-y-0.5">
                      <div className="text-sm font-medium text-white">
                        {submission.user.fullName}
                      </div>
                      <div className="text-xs text-neutral-400">
                        {submission.user.email}
                      </div>
                    </div>
                    <span
                      className={`px-2 py-1 rounded text-xs font-medium border ${getStatusBadgeColor(submission.status)}`}
                    >
                      {formatStatusLabel(submission.status)}
                    </span>
                  </div>

                  <div className="flex justify-between items-center text-xs">
                    <div className="text-neutral-400">
                      Submitted: {formatDateTime(submission.submittedAt)}
                    </div>
                    {submission.sumsubApplicant?.sumsubId && (
                      <a
                        href={`https://cockpit.sumsub.com/checkus/applicant/${submission.sumsubApplicant.sumsubId}/basicInfo?clientId=solana`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1 text-teal-400 hover:text-teal-300"
                      >
                        <OpenInNewIcon className="size-3.5 fill-current" />
                        <span>Sumsub</span>
                      </a>
                    )}
                  </div>
                </div>
              )}
            />

            <Pagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              onPageChange={setPage}
            />
          </div>
        </div>
      </div>

      {selectedSubmissionId && (
        <KybSubmissionDetailModal
          submissionId={selectedSubmissionId}
          onClose={() => selectSubmission(null)}
        />
      )}
    </>
  );
}
